import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

/**
 * UpdatedBadge — subtle freshness signal for indexed money pages.
 *
 * Renders e.g. "Updated May 10, 2026" pulled from /api/top-deals/updated-at,
 * which is the SAME timestamp that drives the sitemap <lastmod> anchor. Users
 * see what Google sees — no lying about freshness.
 *
 * Design rules:
 *   - Subtle by default (text-xs, muted). Pages that want emphasis can pass
 *     `tone="emerald"` for a green tint (matches existing "Updated daily" pills).
 *   - No layout shift: reserves vertical space via `min-h-[1rem]` so the badge
 *     "lands" into pre-allocated space rather than pushing content down.
 *   - API failure = silent hide. Never throws, never shows an error string.
 *   - Do NOT render on noindex/thin pages. The caller decides (we don't gate
 *     here; the badge is a visual freshness signal, not an SEO mechanism).
 *
 * Props:
 *   cachedAt  — optional. If provided (an ISO timestamp string), the component
 *               uses it directly and skips the internal fetch. Use this on
 *               pages that already fetch /top-deals/updated-at for other
 *               reasons, to avoid a duplicate request.
 *   tone     — "muted" (default) | "emerald"
 *   className — extra Tailwind classes to merge in
 *   testId   — data-testid (default: "updated-badge")
 *   prefix   — optional prefix before the date (default: "Updated")
 *   suffix   — optional suffix after the date (e.g. ` · 38 active deals`)
 */
export default function UpdatedBadge({
  cachedAt = null,
  tone = "muted",
  className = "",
  testId = "updated-badge",
  prefix = "Updated",
  suffix = "",
}) {
  const [updatedAt, setUpdatedAt] = useState(cachedAt);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // If the caller passed cachedAt directly, just mirror it — no fetch.
    if (cachedAt !== null) {
      setUpdatedAt(cachedAt);
      setFailed(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/top-deals/updated-at`)
      .then((r) => {
        if (cancelled) return;
        const ts = r?.data?.cached_at;
        if (ts) setUpdatedAt(ts);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [cachedAt]);

  // Reserve space so the badge never causes layout shift when it appears.
  // ~1rem matches text-xs line-height (16px) — close enough that the page
  // doesn't visibly jump when the date hydrates.
  const wrapper = `min-h-[1rem] ${className}`.trim();

  // API failure → render nothing (also no reserved space — the page already
  // has flow without us). This is intentional: if /api is down we shouldn't
  // be carving out a phantom line on the page.
  if (failed) return null;

  // Loading: render an empty placeholder with reserved height. No spinner,
  // no shimmer — we want this to feel like text that "appears", not a UI
  // chrome element that resolves.
  if (!updatedAt) {
    return <p className={wrapper} aria-hidden="true" data-testid={`${testId}-placeholder`} />;
  }

  const date = new Date(updatedAt);
  // Defensive: a malformed timestamp shouldn't break the page.
  if (Number.isNaN(date.getTime())) return null;

  const formatted = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const toneClass =
    tone === "emerald"
      ? "text-xs text-emerald-500 font-medium"
      : "text-xs text-muted-foreground";

  return (
    <p className={`${toneClass} ${wrapper}`} data-testid={testId}>
      {prefix} {formatted}
      {suffix}
    </p>
  );
}
